import { defineStore } from "pinia";
import { ref } from "vue";

import { api, ApiError } from "@/api/client";
import type { PublicConfig } from "@/api/types";

type ConfigState =
  | { kind: "loading" }
  | { kind: "ready"; config: PublicConfig }
  | { kind: "error"; message: string };

export const useConfigStore = defineStore("config", () => {
  const state = ref<ConfigState>({ kind: "loading" });
  let inflight: Promise<void> | null = null;

  async function load(): Promise<void> {
    if (inflight) return inflight;
    state.value = { kind: "loading" };
    inflight = (async () => {
      try {
        const config = await api.config();
        state.value = { kind: "ready", config };
      } catch (err) {
        const message = describeError(err);
        state.value = { kind: "error", message };
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  // Resolve to the loaded PublicConfig, kicking off `load()` if necessary.
  // Used by router navigation guards that need to consult `mode` before
  // a view mounts. Throws on persistent fetch failure so the caller can
  // bail out instead of routing to a half-rendered shell.
  async function ensureLoaded(): Promise<PublicConfig> {
    // Gate the load without returning early so TS doesn't narrow `state.value`
    // before the await.
    if (state.value.kind !== "ready") {
      await load();
    }
    const current = state.value;
    switch (current.kind) {
      case "ready":
        return current.config;
      case "error":
        throw new Error(current.message);
      default:
        throw new Error("config not ready");
    }
  }

  return { state, load, ensureLoaded };
});

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code} (HTTP ${err.status})`;
  if (err instanceof Error) return err.message;
  return "unknown error";
}
